import React, { useState } from 'react';
import { Button, Form, Select, Skeleton } from 'antd';
import { ArrowDownIcon, TimerIcon } from 'icons';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';

import { useAppDispatch, useAppSelector } from 'store';

import {
  globalConst,
  ILanguageData,
  IThereDirectionData,
  useCountdownTimer,
  useDirection,
  useSubjectsName
} from 'modules/global';

import { Visible } from 'components';

import { useUnFinishedTests } from '../../unfinished-tests';
import { useStartDtmTest, useStartVirtualTest } from '../hooks';
import { startTimer } from '../store';

interface IProps {
  languageData?: ILanguageData[];
  directionData?: IThereDirectionData[];
  testCount?: string;
  componentFor?: string;
  languageLoading?: boolean;
  directionLoading?: boolean;
  selectedYear?: string;
  selectedYearId?: number | null;
}

const MainSubjects = ({
  languageLoading,
  languageData,
  directionData,
  testCount,
  componentFor,
  selectedYear,
  selectedYearId
}: IProps) => {
  const t = useTranslations();
  const { push } = useRouter();
  const [form] = Form.useForm();
  const selectedDirectionValue = Form.useWatch('blok', form);
  const [selectedSubjects, setSelectedSubjects] = useState<(number | undefined)[]>([]);

  const { direction, isLoading: isDirectionLoading } = useDirection(selectedDirectionValue);
  const { subjectsName, isLoading: isSubjectsNameLoading } = useSubjectsName();
  const { unFinishedTests, isLoading: unFinishedTestsLoading } = useUnFinishedTests();

  const { mutate: virtualMutation, isLoading: virtualMutationLoading } = useStartVirtualTest();
  const { mutate: dtmMutation, isLoading: dtmMutationLoading } = useStartDtmTest();
  const isLoading = useAppSelector(state => state.routeLoading.isRouteLoading);

  const testStartTime = componentFor === 'dtm' ? unFinishedTests?.dtm?.time : unFinishedTests?.virtual?.time;
  const dispatch = useAppDispatch();

  const { remaining } = useCountdownTimer({ startTime: testStartTime });

  const isContinuing = (): boolean => {
    if (componentFor === 'dtm') {
      dispatch(startTimer());
      return !!unFinishedTests?.dtm?.time;
    }
    if (componentFor === 'virtual') {
      dispatch(startTimer());
      return !!unFinishedTests?.virtual?.time;
    }
    return false;
  };

  const onStartTest = (values: { language_id: number; year_id: number }) => {
    if (isContinuing()) {
      if (componentFor === 'dtm') push(globalConst.pageNames.DTM_TEST);
      else if (componentFor === 'virtual') push(globalConst.pageNames.VIRTUAL_TEST);
      return;
    }
    if (componentFor === 'dtm') {
      dtmMutation({ ...values, year_id: selectedYearId });
    }
    if (componentFor === 'virtual') {
      const virtualStart = {
        language: values.language_id,
        blok1: direction?.blok1_id,
        blok2: direction?.blok2_id
      };

      virtualMutation(virtualStart);
    }
  };

  const handleClick = () => {
    if (isContinuing()) {
      if (componentFor === 'dtm') push(globalConst.pageNames.DTM_TEST);
      else if (componentFor === 'virtual') push(globalConst.pageNames.VIRTUAL_TEST);
    }
  };

  const handleChange = (index: number, value: number) => {
    const newSelected = [...selectedSubjects];

    newSelected[index] = value;
    setSelectedSubjects(newSelected);
  };

  const getFilteredSubjects = (index: number) =>
    subjectsName?.subject.filter(
      subject => !selectedSubjects.includes(subject.id) || selectedSubjects[index] === subject.id
    );

  return (
    <Form
      initialValues={{ language_id: languageData && languageData[0] && languageData[0].id }}
      requiredMark="optional"
      form={form}
      onFinish={onStartTest}
    >
      <div className="m-4 rounded-xl bg-white p-4 sm:m-0">
        {testCount !== '3' && (
          <div className="mb-6 mt-1">
            <div className="inline rounded-lg bg-green-50 p-2 text-sm font-medium text-green-500">
              {testCount === '1' ? 1 : 2} {t('ta_asosiy_fanlar')}
            </div>
          </div>
        )}
        <p className="font-medium">{t('fanlar_majmuasini_tanlash')}</p>
        {componentFor === 'dtm' && testCount === '3' ? (
          <div className="space-y-5">
            <div className="min-h-15 my-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map(i => (
                <Form.Item
                  key={i}
                  layout="vertical"
                  name={`blok_${i + 1}`}
                  label={`${i + 1}-${t('Fan')}`}
                  rules={[{ required: true, message: '' }]}
                >
                  <Select
                    placeholder={t('Fan')}
                    loading={isSubjectsNameLoading}
                    suffixIcon={<ArrowDownIcon />}
                    className="custom-select w-full"
                    onChange={value => handleChange(i, value)}
                    value={selectedSubjects[i]}
                    options={
                      getFilteredSubjects(i)?.map(l => ({
                        value: l.id,
                        label: l.name
                      })) || []
                    }
                  />
                </Form.Item>
              ))}
            </div>
            <Form.Item
              layout="vertical"
              name="language_id"
              className="!mb-15"
              label={t('Talim_tili')}
              rules={[{ required: true }]}
            >
              <Select
                placeholder={t('til')}
                suffixIcon={<ArrowDownIcon />}
                loading={languageLoading}
                className="custom-select w-full"
                options={languageData?.map(l => ({ value: l.id, label: l.name })) || []}
              />
            </Form.Item>
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-1 gap-y-4 sm:grid-cols-2  sm:grid-cols-3 sm:gap-4">
            <div>
              <Form.Item
                layout="vertical"
                label={<label className="mb-0 block text-sm text-gray-800">{t('Talim_tili')}</label>}
                name="language_id"
                rules={[{ required: true }]}
              >
                <Select
                  suffixIcon={<ArrowDownIcon />}
                  className="custom-select w-full"
                  placeholder={t('til')}
                  defaultActiveFirstOption
                  disabled={isContinuing()}
                  options={
                    languageData?.map(l => ({
                      value: l.id,
                      label: l.name
                    })) || []
                  }
                />
              </Form.Item>
            </div>
            <div className="col-span-2">
              {testCount === '1' ? (
                <Form.Item
                  layout="vertical"
                  label={<label className="mb-0 block text-sm text-gray-800">{t('fanlar_majmuasi')}</label>}
                  name="blok_1"
                  rules={[{ required: true, message: '' }]}
                >
                  <Select
                    suffixIcon={<ArrowDownIcon />}
                    className="custom-select w-full"
                    disabled={isContinuing()}
                    placeholder={t('Fan')}
                    loading={isSubjectsNameLoading}
                    options={
                      subjectsName?.subject?.map(d => ({
                        value: d.id,
                        label: d.name
                      })) || []
                    }
                  />
                </Form.Item>
              ) : (
                <Form.Item
                  layout="vertical"
                  label={<label className="mb-0 block text-sm text-gray-800">{t('fanlar_majmuasi')}</label>}
                  name="blok"
                  rules={[{ required: true, message: '' }]}
                >
                  <Select
                    suffixIcon={<ArrowDownIcon />}
                    className="custom-select w-full"
                    disabled={isContinuing()}
                    allowClear
                    placeholder={t('Fan')}
                    loading={isDirectionLoading}
                    options={
                      directionData?.map(d => ({
                        value: d.id,
                        label: d.name
                      })) || []
                    }
                  />
                </Form.Item>
              )}
            </div>
          </div>
        )}
        {direction && isDirectionLoading ? (
          <div className="mt-8 space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <Skeleton.Button active className=" !h-4" />
              <Skeleton.Button active className=" !h-4" />
            </div>
            <div className="flex items-center justify-between">
              <Skeleton.Button active className=" !h-4" />
              <Skeleton.Button active className=" !h-4" />
            </div>
          </div>
        ) : (
          direction &&
          !(testCount === '3') && (
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <p className="font-semibold text-gray-800">1. {direction.blok1_name}</p>
                <p className="text-gray-700">
                  {selectedYear === '2021' ? '20' : '30'} {t('ta')}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-gray-800">2. {direction.blok2_name}</p>
                <p className="text-gray-700">
                  {selectedYear === '2021' ? '20' : '30'} {t('ta')}
                </p>
              </div>
            </div>
          )
        )}

        <div className="rounded-t-4xl h-18 fixed bottom-0 left-0 right-0 mt-5 flex w-full items-center justify-between bg-white px-4 sm:static sm:h-full sm:bg-transparent sm:p-0">
          <div className="flex items-center gap-1 font-medium text-gray-600">
            <div className="text-indigo-600">
              <TimerIcon />
            </div>
            <span>
              {testStartTime ? remaining : '180'} {t('daqiqa')}
            </span>
          </div>

          <Visible
            if={unFinishedTestsLoading}
            else={
              <Visible
                if={isContinuing()}
                else={
                  <Button
                    loading={virtualMutationLoading || dtmMutationLoading || isLoading}
                    htmlType="submit"
                    className="!rounded-lg !border-none !bg-green-500 !p-4 !font-medium !text-white"
                  >
                    {t('boshlash')}
                  </Button>
                }
              >
                <Button
                  loading={isLoading}
                  onClick={handleClick}
                  className="!rounded-lg !border-none !bg-green-500 !p-4 !font-medium !text-white"
                >
                  {t('davom_ettirish')}
                </Button>
              </Visible>
            }
          >
            <Skeleton.Button active className="!h-8 !w-20 !rounded-lg" />
          </Visible>
        </div>
      </div>
    </Form>
  );
};

export default MainSubjects;
